import { useMutation } from "@apollo/client";
import {
	Box,
	Button,
	Flex,
	Grid,
	GridItem,
	Progress,
	Text,
	useToast,
} from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import SubmitPropertyForm1 from "../../../Components/Forms/SubmitPropertyForm1";
import SubmitPropertyForm2 from "../../../Components/Forms/SubmitPropertyForm2";
import SubmitPropertyForm3 from "../../../Components/Forms/SubmitPropertyForm3";
import SubmitPropertyForm4 from "../../../Components/Forms/SubmitPropertyForm4";
import SubmitPropertyForm5 from "../../../Components/Forms/SubmitPropertyForm5";
import SubmitPropertyForm6 from "../../../Components/Forms/SubmitPropertyForm6";
import { SUBMIT_PROPERTY_MUTATION } from "../../../Components/Graphql/Mutations";
import CustomSpinner from "../../../Components/Spinner/CustomSpinner";

const SubmitProperty = () => {
	const toast = useToast();
	const {
		handleSubmit,
		register,
		control,
		formState: { errors },
		setValue,
		getValues,
		reset,
	} = useForm({
		defaultValues: {
			investments: [{ investmentTitle: "", investorDescription: "" }],
			sponsors: [{ sponsorName: "", sponsorDescription: "" }],
			documents: [{ documentName: "", confidentiality: "" }],
		},
	});
	const [step, setStep] = useState(1);
	const [progress, setProgress] = useState(16.66);
	const [propertyFiles, setPropertyFiles] = useState([]);
	const [propertyFileError, setPropertyFileError] = useState();
	const [sponsorFiles, setSponsorFiles] = useState([
		{ file: null, error: null },
	]);
	const [documentFiles, setDocumentFiles] = useState([
		{
			file: null,
			error: null,
		},
	]);

	const investmentFields = useFieldArray({ control, name: "investments" });
	const sponsorFields = useFieldArray({ control, name: "sponsors" });
	const documentFields = useFieldArray({ control, name: "documents" });

	const handleAddMoreInvestment = () => {
		investmentFields.append({});
	};

	const handleRemoveInvestment = (index) => {
		if (investmentFields.fields.length > 1) {
			investmentFields.remove(index);
			const updatedInvestments = getValues("investments").map((item, i) => ({
				...item,
				investmentTitle: item.investmentTitle,
				investorDescription: item.investorDescription,
			}));
			setValue("investments", updatedInvestments);
		}
	};

	const handleAddMoreSponsors = () => {
		sponsorFields.append({});
		setSponsorFiles([...sponsorFiles, { file: null, error: null }]);
	};

	const handleRemoveSponsors = (index) => {
		if (sponsorFields.fields.length > 1) {
			sponsorFields.remove(index);
			const updatedSponsors = getValues("sponsors").map((item, i) => ({
				...item,
				sponsorName: item.sponsorName,
				sponsorDescription: item.sponsorDescription,
			}));
			setValue("sponsors", updatedSponsors);
			let tempSponsorFiles = sponsorFiles;
			tempSponsorFiles.splice(index, 1);
			setSponsorFiles(tempSponsorFiles);
		}
	};

	const handleAddMoreDocuments = () => {
		documentFields.append({});
		setDocumentFiles([...documentFiles, { file: null, error: null }]);
	};

	const handleRemoveDocuments = (index) => {
		if (documentFields.fields.length > 1) {
			documentFields.remove(index);
			const updatedDocuments = getValues("documents").map((item, i) => ({
				...item,
				documentName: item.documentName,
				confidentiality: item.confidentiality,
			}));
			setValue("documents", updatedDocuments);
			let tempDocumentFiles = documentFiles;
			tempDocumentFiles.splice(index, 1);
			setDocumentFiles(tempDocumentFiles);
		}
	};

	const forms = [
		<SubmitPropertyForm1
			register={register}
			errors={errors}
			uploadedImages={propertyFiles}
			setUploadedImages={setPropertyFiles}
			fileError={propertyFileError}
			setFileError={setPropertyFileError}
		/>,
		<SubmitPropertyForm2 register={register} errors={errors} />,
		<SubmitPropertyForm3
			register={register}
			errors={errors}
			fields={investmentFields.fields}
			handleAddMore={handleAddMoreInvestment}
			handleRemove={handleRemoveInvestment}
		/>,
		<SubmitPropertyForm4 register={register} errors={errors} />,
		<SubmitPropertyForm5
			register={register}
			errors={errors}
			uploadedFiles={sponsorFiles}
			setUploadedFiles={setSponsorFiles}
			fields={sponsorFields.fields}
			handleAddMore={handleAddMoreSponsors}
			handleRemove={handleRemoveSponsors}
		/>,
		<SubmitPropertyForm6
			register={register}
			errors={errors}
			uploadedFiles={documentFiles}
			setUploadedFiles={setDocumentFiles}
			fields={documentFields.fields}
			handleAddMore={handleAddMoreDocuments}
			handleRemove={handleRemoveDocuments}
		/>,
	];

	const [submitPropertyMutation, { error, loading, data }] = useMutation(
		SUBMIT_PROPERTY_MUTATION
	);

	useEffect(() => {
		console.log("error:", error);
		console.log("loading:", loading);
		console.log("data:", data);

		if (data != undefined) {
			console.log("data from interested properties: ", data);
			setPropertyFiles([]);
			setPropertyFileError();
			setSponsorFiles({ file: null, error: null });
			setDocumentFiles({ file: null, error: null });
			setStep(1);
			setProgress(16.66);
			toast({
				title: "Property Submitted",
				description: "Property has been successfully submitted",
				status: "success",
				duration: 4000,
				isClosable: true,
				position: "bottom-left",
			});
			reset({
				investments: [{ investmentTitle: "", investorDescription: "" }],
				sponsors: [{ sponsorName: "", sponsorDescription: "" }],
				documents: [{ documentName: "", confidentiality: "Yes" }],
			});
			// setInterestedProperties(data?.getInterestedProperties);
		}
		if (error != undefined) {
			toast({
				title: "error",
				description: error.message,
				status: "error",
				duration: 4000,
				isClosable: true,
				position: "bottom-left",
			});
		}
	}, [data, loading, error]);

	const next = () => {
		setStep(step + 1);
		if (step === 6) {
			setProgress(100);
		} else {
			setProgress(progress + 16.66);
		}
	};

	const onSubmit = async (data) => {
		try {
			if (step !== 6) {
				if (step === 1) {
					if (propertyFiles.length > 0) {
						next();
					} else {
						setPropertyFileError("Property files are required");
					}
				} else if (step === 5) {
					let isTrue = true;
					sponsorFiles.forEach((item, index, arr) => {
						if (item.file === null) {
							isTrue = false;
							arr[index].error = "Sponsor files are required";
						}
					});
					if (isTrue) {
						next();
					}
				} else {
					next();
				}
			} else {
				let isTrue = true;
				documentFiles.forEach((item, index, arr) => {
					if (item.file === null) {
						isTrue = false;
						arr[index].error = "Document files are required";
					}
				});
				if (isTrue) {
					console.log("On submit button called", data);
					const decodedJWT = jwtDecode(sessionStorage.getItem("token"));
					console.log("Decoded jwt", decodedJWT);

					const submitPropertyInput = {
						address: data.propertyAddress,
						brochure: data.propertyBrochure,
						contactInformation: {
							email: data.email,
							firstName: data.firstName,
							lastName: data.lastName,
							phoneNumber: data.phoneNumber,
						},
						creatorEmail: data.creatorEmail,
						description: data.propertyDescription,
						documents: data.documents.map((item, index) => {
							return {
								file: documentFiles[index].file,
								isConfidential: item.confidentiality === "Yes" ? true : false,
								name: item.documentName,
							};
						}),
						images: propertyFiles,
						investmentHighlights: data.investments.map((item) => {
							return {
								title: item.investmentTitle,
								description: item.investorDescription,
							};
						}),
						mapLocation: data.mapLocation,
						name: data.propertyName,
						performanceMetrics: {
							closingDate: data.closingDate ?? "",
							netOperatingIncome: data.netOperatingIncome ?? "",
							pricePerToken: data.pricePerToken ?? "",
							squareMeter: data.squareMeter ?? "",
							targetEquity: data.targetEquity ?? "",
							targetPeriod: data.targetPeriod ?? "",
							targetedAverageCashYield: data.averageCashYield ?? "",
							targetedEquityMultiple: data.targetEquityMultiple ?? "",
							targetedInvestorIRR: data.targetInvestorIRR ?? "",
							units: data.units ?? "",
							yearBuilt: data.yearBuilt ?? "",
						},
						propertyClass: data.propertyClass,
						propertyType: data.propertyType,
						sponsors: data.sponsors.map((item, index) => {
							return {
								name: item.sponsorName,
								description: item.sponsorDescription,
								image: sponsorFiles[index].file,
							};
						}),
						tickerSymbol: data.tickerSymbol,
						riskLevel: data.propertyRiskLevel,
						propertyStatus: data.propertyStatus,
						minimumInvestment: data.minimumInvestment,
						totalShares: data.totalShares,
					};

					console.log("Submit property input: ", submitPropertyInput);
					await submitPropertyMutation({
						variables: { submitPropertyInput: submitPropertyInput },
					});
				}
			}
		} catch (error) {
			console.log("Error: ", error);
		}
	};

	return (
		<Box w="100%">
			<Text fontSize="20px" color="#333" marginLeft="30px" marginBottom="10px">
				Add a Property
			</Text>
			<Box
				color="#315B84"
				background="#FFFFFF"
				padding="20px 25px 15px 25px"
				w="100%"
				borderRadius="15px"
			>
				<Box rounded="lg" maxWidth={800} p={{ base: 1, lg: 6 }}>
					<Progress hasStripe value={progress} mb="5%" mx="5%" isAnimated />
					<form onSubmit={handleSubmit(onSubmit)}>
						{forms[step - 1]}
						<Grid templateColumns={"repeat(2, 1fr)"} gap={5} mt="5%">
							<GridItem colSpan={{ base: 2, lg: 1 }}>
								<Flex>
									<Button
										onClick={() => {
											setStep(step - 1);
											setProgress(progress - 16.66);
										}}
										isDisabled={step === 1}
										variant="solid"
										background={"#1C3E64"}
										color="white"
										w="7rem"
										mr="5%"
										_hover={{ bgColor: "#002B5B" }}
									>
										Back
									</Button>
									<Button
										type="submit"
										w="7rem"
										isDisabled={step === 6}
										// colorScheme="teal"
										variant="outline"
										borderColor={"#1C3E64"}
									>
										Next
									</Button>
								</Flex>
							</GridItem>

							<GridItem
								colSpan={{ base: 2, lg: 1 }}
								display={"flex"}
								justifyContent={{ base: "flex-start", lg: "flex-end" }}
							>
								{step === 6 ? (
									<Button
										type="submit"
										w="7rem"
										colorScheme="red"
										variant="solid"
									>
										Submit
									</Button>
								) : null}
							</GridItem>
						</Grid>
					</form>
				</Box>
			</Box>
			<CustomSpinner isSpinnerOpen={loading} />
		</Box>
	);
};

export default SubmitProperty;
